// Shared imports
const { TYPES: modelTypes } = require('shared/model-types')
// Client lib imports
const businessEvents = require('client/lib/business-events')
const windowLib = require('client/lib/window')
const { send: ajax } = require('client/lib/request')
// Client V2 common imports
const { FetchStateEnum } = require('client-v2/common/utils/enums')
const extension = require('client-v2/common/utils/extension')
const { loadThreads } = require('client-v2/notifications/containers/shared-inbox/actions')
// Sibling imports
const socialMediums = require('./mediums')

// Action types

const HIDE = 'SHARE_MODAL_HIDE'
const FRIENDS_FAILED = 'SHARE_MODAL_FRIENDS_FAILED'
const FRIENDS_LOADED = 'SHARE_MODAL_FRIENDS_LOADED'
const FRIENDS_LOADING = 'SHARE_MODAL_FRIENDS_LOADING'
const FRIENDS_SEARCH = 'SHARE_MODAL_FRIENDS_SEARCH'
const FRIENDS_TOGGLE = 'SHARE_MODAL_FRIENDS_TOGGLE'
const SHOW = 'SHARE_MODAL_SHOW'
const SUBMIT_LOADING = 'SHARE_MODAL_SUBMIT_LOADING'
const SUBMIT_LOADED = 'SHARE_MODAL_SUBMIT_LOADED'
const SUBMIT_FAILED = 'SHARE_MODAL_SUBMIT_FAILED'

// Action creators

function setSearchText(searchText) {
  return {
    type: FRIENDS_SEARCH,
    payload: { searchText }
  }
}

function hide() {
  return {
    type: HIDE
  }
}

function toggleFriend(userId) {
  return {
    type: FRIENDS_TOGGLE,
    payload: { userId }
  }
}

// Action dispatchers

function shareWithFriends(caption) {
  return async function (dispatch, getState) {
    const { feedItem: { article }, friends } = getState().shareModal
    const { sharedInbox } = getState()
    const friendIds = friends.filter(f => f.isSelected).map(f => f.id).toJS()
    dispatch({
      type: SUBMIT_LOADING
    })
    const { 'url_id': urlId } = article
    try {
      const { friends } = await ajax({
        url: `/api/v0.1/share-with`,
        method: 'post',
        contentType: 'application/json',
        data: {
          destinationUserIds: friendIds,
          'url_id': urlId,
          caption
        }
      })
      extension.clearUrlCache(urlId)
      dispatch({
        type: SUBMIT_LOADED,
        payload: { friends }
      })
      if (sharedInbox) {
        dispatch(loadThreads())
      }
    } catch (err) {
      dispatch({
        type: SUBMIT_FAILED
      })
      throw err
    }
  }
}

function show({ model }) {
  return async function (dispatch) {
    dispatch({
      type: SHOW,
      payload: { model }
    })
    if (model.type === modelTypes.ARTICLE) {
      dispatch(loadFriends())
    }
  }
}

function loadFriends() {
  return async function (dispatch, getState) {
    const { user, shareModal } = getState()
    // Logged-out users don't have friends; only fetch once
    if (!user.loggedIn || shareModal.friendsState !== FetchStateEnum.EMPTY) {
      return
    }
    dispatch({
      type: FRIENDS_LOADING
    })
    try {
      dispatch({
        type: FRIENDS_LOADED,
        payload: {
          friends: await getShareableFriends(user.id)
        }
      })
    } catch (err) {
      dispatch({
        type: FRIENDS_FAILED
      })
      throw err
    }
  }
}

function share({ href, model, shareType }) {
  // This function cannot be async because of window.open
  return function (dispatch, getState) {
    const { user } = getState()
    if (shareType === 'email') {
      /**
       * `mailto:` links need to be handled by the default `<a href>` click behavior
       * @see https://app.asana.com/0/226524042134185/1137183849467687/f
       */
      return trackShare(model, shareType, user.id)
    }
    const shareMedium = socialMediums.find(medium => medium.id === shareType)
    const [ width, height ] = shareMedium.windowSize
    const features = windowLib.centeredWindowFeatures(width, height)
    windowLib.openWindow(href, `share-${shareType}`, features)
    return trackShare(model, shareType, user.id)
  }
}

function copy({ input, model }) {
  // This function cannot be async because of document.execCommand
  return function (dispatch, getState) {
    const { env, user } = getState()

    // Link text element is now hidden - need to create a temporary dummy to select
    const tempInput = document.createElement('textarea')
    document.body.appendChild(tempInput)
    tempInput.value = input.value

    /**
     * iOS has a special process for copying text to the clipboard
     * @see https://stackoverflow.com/a/34046084
     */
    if (env.browser.get('ios')) {
      const preserveContentEditable = tempInput.contentEditable
      const preserveReadOnly = tempInput.readOnly
      const range = document.createRange()

      tempInput.contentEditable = true
      tempInput.readOnly = false
      range.selectNodeContents(tempInput)

      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
      tempInput.setSelectionRange(0, tempInput.value.length)

      tempInput.contentEditable = preserveContentEditable
      tempInput.readOnly = preserveReadOnly
    } else {
      tempInput.select()
    }
    document.execCommand('copy')
    document.body.removeChild(tempInput)
    return trackShare(model, 'copy', user.id)
  }
}

module.exports = {
  // Action types
  HIDE,
  FRIENDS_FAILED,
  FRIENDS_LOADED,
  FRIENDS_LOADING,
  FRIENDS_SEARCH,
  FRIENDS_TOGGLE,
  SHOW,
  SUBMIT_LOADING,
  SUBMIT_LOADED,
  SUBMIT_FAILED,
  // Action creators
  hide,
  setSearchText,
  toggleFriend,
  // Action dispatchers
  copy,
  share,
  shareWithFriends,
  show
}

// Helpers

async function getShareableFriends(userId, page = 1) {
  const { friends } = await ajax({
    url: `/api/v0.1/user/${userId}/shareable-friends/${page}`,
    method: 'post'
  })
  if (friends.length) {
    const moreFriends = await getShareableFriends(userId, page + 1)
    return [ ...friends, ...moreFriends ]
  }
  return []
}

async function trackShare(model, shareType, userId) {
  switch (model.type) {
    case modelTypes.ARTICLE: return Promise.all([
      businessEvents.trackProtobuf('TrackExternalUrlPrivateShareEvent', {
        senderUserId: userId,
        urlId: model['url_id'],
        shareType
      }),
      businessEvents.trackSegment('share-external', {
        type: shareType,
        'url_id': model['url_id']
      })
    ])
    case modelTypes.MIX: return Promise.all([
      businessEvents.trackProtobuf('TrackExternalMixShareEvent', {
        senderUserId: userId,
        mixId: model.id,
        shareType
      }),
      businessEvents.trackSegment('share-external', {
        type: shareType,
        'mix_id': model.id
      })
    ])
    case modelTypes.USER: return Promise.all([
      businessEvents.trackProtobuf('TrackExternalUserShareEvent', {
        senderUserId: userId,
        sharedUserId: model.id,
        shareType
      }),
      businessEvents.trackSegment('share-external', {
        type: shareType,
        'user_id': model.id
      })
    ])
  }
}
